import React, { useState, useEffect } from 'react';
import { entitiesApi, subCategoriesApi, entityBankAccountsApi, partnerAccountsApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { PlusCircle, Sparkles } from 'lucide-react';
import { CATEGORIES, PAYMENT_MODES, SUB_CAT_CATEGORIES, SUB_CAT_LABEL, SUB_CAT_PLURAL, getEntityTypeForCategory } from '@/constants/categories';

export function EntryFormDialog({ open, onOpenChange, editing, initialForm, sites, entities, subCategories, onSubmit, onEntitiesChanged, onSubCategoriesChanged }) {
  const [form, setForm] = useState(initialForm);
  const [showNewEntity, setShowNewEntity] = useState(false);
  const [newEntityName, setNewEntityName] = useState('');
  const [showNewSubCat, setShowNewSubCat] = useState(false);
  const [newSubCatName, setNewSubCatName] = useState('');
  const [entityBankAccounts, setEntityBankAccounts] = useState([]);

  useEffect(() => {
    setForm(initialForm);
    setShowNewEntity(false);
    setShowNewSubCat(false);
    setNewEntityName('');
    setNewSubCatName('');
  }, [initialForm, open]);

  useEffect(() => {
    if (form.entity_id) {
      // Fetch both entity bank accounts and partner accounts, merge them
      Promise.all([
        entityBankAccountsApi.list(form.entity_id).catch(() => ({ data: [] })),
        partnerAccountsApi.list().catch(() => ({ data: [] })),
      ]).then(([ebaRes, paRes]) => {
        const entityAccs = ebaRes.data || [];
        const partnerAccs = (paRes.data || [])
          .filter(a => a.partner_id === form.entity_id)
          .map(a => ({ _id: `pa_${a._id}`, account_name: a.account_name, bank_name: a.bank_name || '', account_number: a.account_number || '' }));
        setEntityBankAccounts([...entityAccs, ...partnerAccs]);
      });
      // Auto-suggest sub-category from entity's supplies
      const ent = entities.find(e => e._id === form.entity_id);
      if (ent?.supplies?.length > 0 && SUB_CAT_CATEGORIES.includes(form.category)) {
        const matching = ent.supplies.filter(s => s.category === form.category);
        // Auto-fill if exactly 1 matching supply and sub_category is empty
        if (matching.length === 1 && !form.sub_category) {
          setForm(f => ({ ...f, sub_category: matching[0].sub_category }));
        }
      }
    } else {
      setEntityBankAccounts([]);
    }
  }, [form.entity_id]);

  const getEntityTypeForCat = getEntityTypeForCategory;

  const categoryEntities = entities.filter(e => {
    const t = getEntityTypeForCat(form.category);
    return t ? e.entity_type === t : true;
  });

  const filteredSubCats = subCategories.filter(sc => sc.parent_category === form.category);
  const showSubCategoryField = SUB_CAT_CATEGORIES.includes(form.category);
  const entityLabel = SUB_CAT_CATEGORIES.includes(form.category) ? 'Agency / Vendor' :
    form.category === 'partner_fund' ? 'Partner' :
    form.category === 'customer_fund' ? 'Customer' : 'Person / Entity';

  // Derive suggested sub-categories from selected entity's supplies
  const selectedEntity = form.entity_id ? entities.find(e => e._id === form.entity_id) : null;
  const entitySupplies = selectedEntity?.supplies || [];
  const suggestedSubCatNames = entitySupplies
    .filter(s => s.category === form.category)
    .map(s => s.sub_category);
  const suggestedSubCats = filteredSubCats.filter(sc => suggestedSubCatNames.includes(sc.name));
  const otherSubCats = filteredSubCats.filter(sc => !suggestedSubCatNames.includes(sc.name));

  const handleSubmit = async (e) => {
    e.preventDefault();
    await onSubmit(form, editing);
  };

  const handleQuickCreateEntity = async () => {
    if (!newEntityName.trim()) return;
    const entityType = SUB_CAT_CATEGORIES.includes(form.category) ? 'vendor' :
      form.category === 'partner_fund' ? 'partner' :
      form.category === 'customer_fund' ? 'customer' : 'person';
    try {
      const { data } = await entitiesApi.quickCreate({ name: newEntityName.trim(), entity_type: entityType });
      onEntitiesChanged?.();
      setForm(f => ({ ...f, entity_id: data._id, entity_name: data.name }));
      setShowNewEntity(false);
      setNewEntityName('');
    } catch {}
  };

  const handleQuickCreateSubCat = async () => {
    if (!newSubCatName.trim()) return;
    try {
      const { data } = await subCategoriesApi.create({ name: newSubCatName.trim(), parent_category: form.category });
      onSubCategoriesChanged?.();
      setForm(f => ({ ...f, sub_category: data.name }));
      setShowNewSubCat(false);
      setNewSubCatName('');
    } catch {}
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editing ? 'Edit Entry' : 'Add New Entry'}</DialogTitle>
          <DialogDescription className="sr-only">{editing ? 'Edit entry details' : 'Fill in the details to add a new entry'}</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4" data-testid="entry-form">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground font-semibold">Date</Label>
              <Input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} required className="mt-1.5" data-testid="entry-date-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground font-semibold">Site</Label>
              <Select value={form.site_id} onValueChange={v => setForm(f => ({ ...f, site_id: v }))}>
                <SelectTrigger className="mt-1.5" data-testid="entry-site-select"><SelectValue placeholder="Select site" /></SelectTrigger>
                <SelectContent>{sites.map(s => <SelectItem key={s._id} value={s._id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground font-semibold">Type</Label>
              <Select value={form.entry_type} onValueChange={v => setForm(f => ({ ...f, entry_type: v }))}>
                <SelectTrigger className="mt-1.5" data-testid="entry-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="expense">Money Out (Expense)</SelectItem>
                  <SelectItem value="income">Money In (Receipt)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground font-semibold">Category</Label>
              <Select value={form.category} onValueChange={v => setForm(f => ({ ...f, category: v, sub_category: '', entity_id: '', entity_name: '' }))}>
                <SelectTrigger className="mt-1.5" data-testid="entry-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          {showSubCategoryField && (
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground font-semibold">
                {SUB_CAT_LABEL[form.category] || 'Sub-Type'}
              </Label>
              {!showNewSubCat ? (
                <div className="flex gap-2 mt-1.5">
                  <Select value={form.sub_category} onValueChange={v => setForm(f => ({ ...f, sub_category: v === 'none' ? '' : v }))}>
                    <SelectTrigger className="flex-1" data-testid="entry-sub-category-select">
                      <SelectValue placeholder={`Select ${(SUB_CAT_LABEL[form.category] || 'sub-type').toLowerCase()}`} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">-- None --</SelectItem>
                      {suggestedSubCats.length > 0 && (
                        <>
                          <div className="px-2 py-1.5 text-[10px] font-semibold uppercase tracking-widest text-amber-600 dark:text-amber-400 flex items-center gap-1 select-none">
                            <Sparkles className="w-3 h-3" /> Vendor Supplies
                          </div>
                          {suggestedSubCats.map(sc => (
                            <SelectItem key={`sug-${sc._id}`} value={sc.name} data-testid={`suggested-sub-cat-${sc._id}`}>
                              {sc.name}
                            </SelectItem>
                          ))}
                          {otherSubCats.length > 0 && (
                            <div className="px-2 py-1.5 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground select-none border-t border-border mt-1 pt-1.5">
                            All {SUB_CAT_PLURAL[form.category] || 'Items'}
                            </div>
                          )}
                        </>
                      )}
                      {(suggestedSubCats.length > 0 ? otherSubCats : filteredSubCats).map(sc => <SelectItem key={sc._id} value={sc.name}>{sc.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Button type="button" variant="outline" size="icon" onClick={() => setShowNewSubCat(true)} title="Add new" data-testid="add-new-sub-category-btn">
                    <PlusCircle className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <div className="flex gap-2 mt-1.5" data-testid="new-sub-category-inline">
                  <Input value={newSubCatName} onChange={e => setNewSubCatName(e.target.value)} placeholder={`New ${(SUB_CAT_LABEL[form.category] || 'sub-type').toLowerCase()} name`} className="flex-1" data-testid="new-sub-category-input" autoFocus />
                  <Button type="button" size="sm" onClick={handleQuickCreateSubCat} data-testid="save-new-sub-category-btn">Add</Button>
                  <Button type="button" variant="outline" size="sm" onClick={() => { setShowNewSubCat(false); setNewSubCatName(''); }}>Cancel</Button>
                </div>
              )}
              {/* Vendor supply hint */}
              {suggestedSubCats.length > 0 && !form.sub_category && (
                <div className="flex items-center gap-1.5 mt-1.5 text-xs text-amber-600 dark:text-amber-400" data-testid="supply-hint">
                  <Sparkles className="w-3 h-3" />
                  <span>This vendor supplies: {suggestedSubCatNames.join(', ')}</span>
                </div>
              )}
            </div>
          )}

          <div>
            <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground font-semibold">{entityLabel}</Label>
            {!showNewEntity ? (
              <div className="flex gap-2 mt-1.5">
                <Select value={form.entity_id} onValueChange={v => {
                  if (v === 'none') { setForm(f => ({ ...f, entity_id: '', entity_name: '' })); return; }
                  const ent = entities.find(e => e._id === v);
                  setForm(f => ({ ...f, entity_id: v, entity_name: ent?.name || '' }));
                }}>
                  <SelectTrigger className="flex-1" data-testid="entry-entity-select">
                    <SelectValue placeholder={`Select ${entityLabel.toLowerCase()}`} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">-- None --</SelectItem>
                    {categoryEntities.map(e => <SelectItem key={e._id} value={e._id}>{e.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button type="button" variant="outline" size="icon" onClick={() => setShowNewEntity(true)} title="Add new agency / person" data-testid="add-new-entity-btn">
                  <PlusCircle className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <div className="flex gap-2 mt-1.5" data-testid="new-entity-inline">
                <Input value={newEntityName} onChange={e => setNewEntityName(e.target.value)} placeholder={`New ${entityLabel.toLowerCase()} name`} className="flex-1" data-testid="new-entity-name-input" autoFocus />
                <Button type="button" size="sm" onClick={handleQuickCreateEntity} data-testid="save-new-entity-btn">Add</Button>
                <Button type="button" variant="outline" size="sm" onClick={() => { setShowNewEntity(false); setNewEntityName(''); }}>Cancel</Button>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground font-semibold">Payment Mode</Label>
              <Select value={form.payment_mode} onValueChange={v => setForm(f => ({ ...f, payment_mode: v, bank_account_id: '', bank_account_name: '' }))}>
                <SelectTrigger className="mt-1.5" data-testid="entry-payment-select"><SelectValue /></SelectTrigger>
                <SelectContent>{PAYMENT_MODES.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground font-semibold">Amount ({'\u20B9'})</Label>
              <Input type="number" step="0.01" min="0" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} required className="mt-1.5 font-mono" data-testid="entry-amount-input" />
            </div>
          </div>

          {form.payment_mode === 'bank' && form.entity_id && entityBankAccounts.length > 0 && (
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground font-semibold">Bank Account</Label>
              <Select value={form.bank_account_id} onValueChange={v => {
                if (v === 'none') { setForm(f => ({ ...f, bank_account_id: '', bank_account_name: '' })); return; }
                const acc = entityBankAccounts.find(a => a._id === v);
                setForm(f => ({ ...f, bank_account_id: v, bank_account_name: acc ? `${acc.account_name}${acc.bank_name ? ' - ' + acc.bank_name : ''}` : '' }));
              }}>
                <SelectTrigger className="mt-1.5" data-testid="entry-bank-account-select"><SelectValue placeholder="Select bank account" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">-- No specific account --</SelectItem>
                  {entityBankAccounts.map(a => (
                    <SelectItem key={a._id} value={a._id}>
                      {a.account_name}{a.bank_name ? ` (${a.bank_name})` : ''}{a.account_number ? ` - ${a.account_number}` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div>
            <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground font-semibold">Notes</Label>
            <Textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Description or notes..." className="mt-1.5" rows={2} data-testid="entry-notes-input" />
          </div>

          <Button type="submit" className="w-full" data-testid="entry-submit-btn">{editing ? 'Update Entry' : 'Add Entry'}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
